import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import {
  ArrowLeft, Bot, User, Headset, Phone, Play, FileText, Smile,
  CalendarClock, ExternalLink, AlertTriangle, Clock,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { callStatusMeta, sentimentMeta, fmtDuration } from "@/config/voice";
import { fmtDateTime } from "@/config/crm";

export default function CallDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/voice/calls/${id}`); setData(data); }
    catch { toast.error("Call not found"); navigate("/calls"); }
    finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  if (loading || !data) return (
    <div className="space-y-6"><Skeleton className="h-8 w-48" /><div className="grid gap-6 lg:grid-cols-3"><Skeleton className="h-96 lg:col-span-2" /><Skeleton className="h-96" /></div></div>
  );

  const c = data.call;
  const st = callStatusMeta(c.status);
  const sm = sentimentMeta(c.sentiment);

  return (
    <div className="animate-fade-up space-y-6" data-testid="call-detail-page">
      <div className="flex items-center gap-3">
        <Button variant="outline" size="icon" onClick={() => navigate("/calls")} data-testid="back-to-calls"><ArrowLeft className="h-4 w-4" /></Button>
        <div>
          <div className="flex items-center gap-2">
            <h1 className="font-heading text-2xl font-semibold tracking-tight text-slate-900" data-testid="call-detail-name">{c.contact_name}</h1>
            <Badge className={cn(st.badge)} data-testid="call-status-badge">{st.label}</Badge>
            {c.sentiment && <Badge className={cn(sm.badge)} data-testid="call-sentiment-badge">{sm.label}</Badge>}
          </div>
          <p className="mt-1 font-mono text-sm text-slate-500">{c.caller_number} · {c.direction} · {fmtDuration(c.duration)}</p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card className="shadow-sm">
            <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 font-heading text-base"><FileText className="h-4 w-4 text-emerald-600" /> AI Summary</CardTitle></CardHeader>
            <CardContent>
              <p className="text-sm text-slate-700" data-testid="call-summary">{c.summary || "Call in progress — end the call to generate a summary."}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {c.intent && <Badge variant="secondary" className="bg-slate-100 capitalize text-slate-600">Intent: {c.intent.replace(/_/g, " ")}</Badge>}
                {(c.objections || []).map((o, i) => (
                  <Badge key={i} variant="secondary" className="bg-amber-50 text-amber-700" data-testid="call-objection"><AlertTriangle className="mr-1 h-3 w-3" />{o}</Badge>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-sm">
            <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 font-heading text-base"><Play className="h-4 w-4 text-emerald-600" /> Recording</CardTitle></CardHeader>
            <CardContent>
              {c.recording_url ? (
                <audio controls src={c.recording_url} className="w-full" data-testid="call-recording-player" />
              ) : <p className="text-sm text-slate-400">No recording available (recordings are captured with a live telephony provider).</p>}
            </CardContent>
          </Card>

          <Card className="shadow-sm" data-testid="call-transcript">
            <CardHeader className="pb-2"><CardTitle className="font-heading text-base">Transcript</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {data.transcript.length === 0 ? <p className="text-sm text-slate-400">No transcript.</p>
                : data.transcript.map((t, i) => (
                  <div key={i} className={cn("flex", t.speaker === "caller" ? "justify-start" : "justify-end")} data-testid="transcript-line">
                    <div className={cn("max-w-[80%] rounded-lg px-3 py-2 text-sm",
                      t.speaker === "caller" ? "border border-slate-200 bg-white text-slate-800"
                        : t.speaker === "ai" ? "bg-emerald-600 text-white" : "bg-slate-800 text-white")}>
                      <p className="mb-0.5 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider opacity-80">
                        {t.speaker === "caller" ? <><User className="h-3 w-3" /> Caller</> : t.speaker === "ai" ? <><Bot className="h-3 w-3" /> Wellora AI</> : <><Headset className="h-3 w-3" /> Staff</>}
                      </p>
                      <p className="whitespace-pre-wrap">{t.text}</p>
                      <p className="mt-1 text-[10px] opacity-60">{fmtDateTime(t.at)}</p>
                    </div>
                  </div>
                ))}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="shadow-sm">
            <CardHeader className="pb-2"><CardTitle className="font-heading text-base">Call Details</CardTitle></CardHeader>
            <CardContent className="space-y-3 text-sm">
              <p className="flex justify-between"><span className="text-slate-400">Provider</span><span className="capitalize text-slate-700">{c.provider}</span></p>
              <p className="flex justify-between"><span className="text-slate-400">Started</span><span className="text-slate-700">{fmtDateTime(c.start_time)}</span></p>
              <p className="flex justify-between"><span className="text-slate-400">Duration</span><span className="text-slate-700">{fmtDuration(c.duration)}</span></p>
              <p className="flex justify-between"><span className="text-slate-400">Assigned</span><span className="text-slate-700">{c.owner_name || "—"}</span></p>
              {c.handoff_requested && (
                <div className="rounded-lg border border-amber-100 bg-amber-50/60 p-3" data-testid="call-handoff">
                  <p className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-amber-700"><Headset className="h-3.5 w-3.5" /> Human Handoff</p>
                  <p className="mt-1 text-sm text-slate-700">{c.handoff_reason}</p>
                  <p className="text-xs text-slate-400">{fmtDateTime(c.handoff_at)}</p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="shadow-sm">
            <CardHeader className="pb-2"><CardTitle className="font-heading text-base">CRM Context</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {data.patient ? (
                <div className="rounded-lg border border-slate-100 p-3">
                  <p className="text-xs uppercase tracking-wider text-slate-400">Patient</p>
                  <p className="text-sm font-medium text-slate-900">{data.patient.full_name}</p>
                  <p className="font-mono text-xs text-slate-400">{data.patient.patient_code}</p>
                  <Button variant="outline" size="sm" className="mt-2 w-full" onClick={() => navigate(`/patients/${data.patient.id}`)} data-testid="call-view-patient"><ExternalLink className="h-3.5 w-3.5" /> Patient 360</Button>
                </div>
              ) : data.lead ? (
                <div className="rounded-lg border border-slate-100 p-3">
                  <p className="text-xs uppercase tracking-wider text-slate-400">Lead</p>
                  <p className="text-sm font-medium text-slate-900">{data.lead.full_name}</p>
                  <Button variant="outline" size="sm" className="mt-2 w-full" onClick={() => navigate(`/leads/${data.lead.id}`)} data-testid="call-view-lead"><ExternalLink className="h-3.5 w-3.5" /> Open Lead</Button>
                </div>
              ) : <p className="text-sm text-slate-400">No linked CRM record.</p>}

              {data.appointment && (
                <div className="rounded-lg border border-emerald-100 bg-emerald-50/40 p-3" data-testid="call-appointment">
                  <p className="flex items-center gap-1 text-xs uppercase tracking-wider text-slate-400"><CalendarClock className="h-3.5 w-3.5" /> Appointment Booked</p>
                  <p className="text-sm font-medium text-slate-900">{data.appointment.service_name}</p>
                  <p className="text-xs text-slate-500">{data.appointment.doctor_name} · {data.appointment.date} {data.appointment.start_time}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
